/**
 * MyListHeader component will show a Title, Subtitle and Edit/Share icons for View and Share Wishlists
 *
 * @module views/components/MyListHeader
 * @memberof -Common
 */
import './MyListHeader.scss';

import React, { useCallback, useEffect, useRef, useState } from 'react';

import PropTypes from 'prop-types';

import Icon from '@ulta/core/components/Icon/Icon';
import Text from '@ulta/core/components/Text/Text';
import { useDeviceInflection } from '@ulta/core/providers/InflectionProvider/InflectionProvider';
import { useOverlay } from '@ulta/core/providers/OverlayProvider/OverlayProvider';
import { spreadProps } from '@ulta/core/utils/dom/dom';
import { handleEmptyObjects } from '@ulta/core/utils/handleEmptyObjects/handleEmptyObjects';

import Button from '@ulta/modules/Button/Button';
import GridContainer from '@ulta/modules/GridContainer/GridContainer';
import MyListsForm from '@ulta/modules/MyListsForm/MyListsForm';

import * as utils from '@ulta/components/MyListHeader/MyListHeader';
import ShareList from '@ulta/components/ShareList/ShareList';

/**
 * Represents an MyListHeader component
 * @method
 * @param {MyListHeaderProps} props - React properties passed from composition
 * @returns MyListHeader
 */
const MyListHeader = ( props ) => {
  const {
    title,
    subTitle,
    shareAction,
    saveAction,
    shareDetails,
    editAction,
    copyAction,
    invokeMutation,
    id,
    editIcon,
    myListsForm,
    shareIcon,
    listId,
    loading
  } = props;

  const { openOverlay, updateContent, displayOverlay } = useOverlay();
  const { breakpoint } = useDeviceInflection();

  const flyoutOpenerRef = useRef( null );
  const flyoutActionClicked = useRef( false );

  const [flyoutConfig, setFlyoutConfig] = useState( null );

  const isMobileDevice = breakpoint.CURRENT_BREAKPOINT === 'SM';

  const updateOverlayContent = useCallback(
    composeUpdateOverlayContent(
      {
        flyoutConfig,
        myListsForm,
        saveAction,
        shareAction,
        id,
        copyAction,
        title,
        shareDetails,
        loading,
        flyoutActionClicked
      },
      {
        updateContent,
        invokeMutation
      }
    ),
    [
      shareAction, flyoutConfig, myListsForm,
      saveAction, updateContent, invokeMutation,
      id, copyAction, title, shareDetails,
      loading, flyoutActionClicked
    ]
  );

  const onOpenOverlay = useCallback(
    composeOnOpenOverlay(
      { flyoutOpenerRef, flyoutConfig },
      { openOverlay }
    ),
    [flyoutOpenerRef, flyoutConfig, openOverlay]
  );

  const handleFlyoutAction = useCallback(
    ( config ) => {
      setFlyoutConfig( config );
      flyoutActionClicked.current = true;
      onOpenOverlay();
    },
    [onOpenOverlay]
  );

  const handleShare = useCallback(
    utils.composeOnShare(
      { isMobileDevice },
      { handleFlyoutAction }
    ),
    [handleFlyoutAction, isMobileDevice]
  );

  const handleEdit = useCallback(
    utils.composeOnEdit(
      { handleFlyoutAction }
    ),
    [handleFlyoutAction]
  );

  useEffect( () => {
    if( !displayOverlay ){
      setFlyoutConfig( null );
      flyoutActionClicked.current = false;
    }
  }, [displayOverlay] );

  useEffect( () => {
    updateOverlayContent();
  }, [updateOverlayContent] );

  if( !title ){
    return;
  }

  return (
    <GridContainer fullBleedContentBackgroundColor='white'>
      <div className='MyListHeader' ref={ flyoutOpenerRef }>
        <div className='MyListHeader__HeadingContainer'>
          <div className='MyListHeader__Heading'>
            { title && (
              <Text
                htmlTag='h2'
                textStyle='title-lg'
              >
                { title }
              </Text>
            ) }
            { subTitle && (
              <Text
                htmlTag='span'
                textStyle='body-2'
              >
                { subTitle }
              </Text>
            ) }
          </div>
          <div className='MyListHeader__ButtonIcons'>
            { shareAction && (
              <Button
                action={ shareAction }
                variant='link'
                ariaLabel={ shareAction.accessibility?.label }
                onClick={ () => handleShare( { mode: MY_LIST_FORM_MODES.SHARE, data: { ...shareDetails, listTitle: title } } ) }
                iconName={ shareIcon }
                iconSize='lg'
                iconOnly
              />
            ) }
            { editAction && (
              <Button
                action={ editAction }
                variant='link'
                ariaLabel={ editAction.accessibility?.label }
                onClick={ () => handleEdit( { mode: MY_LIST_FORM_MODES.EDIT, data: { ...myListsForm, listId } } ) }
                iconName={ editIcon }
                iconSize='lg'
                iconOnly
              />
            ) }
          </div>
        </div>
      </div>
    </GridContainer>
  );
};

/**
 * Composes a function to update the overlay content with the MyListsForm.
 *
 * @param {object} data - Data needed to configure the MyListsForm.
 * @param {object} methods - Methods for updating the overlay content.
 * @returns {Function} A function that updates the overlay content.
 */
export const composeUpdateOverlayContent = ( data, methods ) => () => {
  const { flyoutConfig, myListsForm, saveAction, id, copyAction, title, shareDetails, loading, flyoutActionClicked } = handleEmptyObjects( data );

  const { updateContent, invokeMutation } = handleEmptyObjects( methods );

  if( !flyoutConfig ){
    return;
  }

  const isEdit = flyoutConfig.mode === MY_LIST_FORM_MODES.EDIT;
  const isShare = flyoutConfig.mode === MY_LIST_FORM_MODES.SHARE;
  const listData = flyoutConfig.data;

  let content;

  if( isShare ){
    content = (
      <ShareList
        { ...spreadProps( { ...shareDetails } ) }
        copyAction={ copyAction }
      />
    );
  }
  else {
    let formMessages =  myListsForm?.errorMessages;
    if( flyoutActionClicked.current ){
      formMessages = [];
      flyoutActionClicked.current = false;
    }
    const formProps = {
      myListsForm: {
        ...myListsForm,
        errorMessage: formMessages,
        ...( isEdit &&
            {
              listName: {
                ...myListsForm.listName,
                value: title
              },
              listId: listData?.listId
            } )
      },
      loading,
      saveAction,
      invokeMutation,
      contentId: id,
      componentKey: `${id}:${Math.random()}`
    };

    content = (
      <MyListsForm { ...spreadProps( formProps ) } />
    );
  }
  updateContent( content );
};

/**
 * Method to handle composeOnOpenOverlay
 * @param {object} data args
 * @param {object} data.flyoutOpenerRef - buttonRef
 * @param {object} data.flyoutConfig - Ref to the searchModal
 * @param {object} methods args
 * @param {function} methods.openOverlay openOverlay
 */
export const composeOnOpenOverlay = ( data, methods ) => () => {
  const { flyoutOpenerRef, flyoutConfig, headline } = handleEmptyObjects( data );
  const { openOverlay } = handleEmptyObjects( methods );

  if( !openOverlay ){
    return;
  }
  openOverlay( {
    type: 'flyout',
    crossButtonVisibility: true,
    opener: flyoutOpenerRef,
    content: flyoutConfig,
    ariaLabel: headline
  } );
};

/**
 * Composable function to handle share logic.
 * It decides whether to use the native Web Share API or a fallback flyout.
 * @param {object} data - Dependencies for the function.
 * @param {boolean} data.isMobileDevice - Flag indicating if the current device is mobile.
 * @param {object} methods - Methods to be called.
 * @param {function} methods.handleFlyoutAction - The function to call to open the share flyout on non-mobile devices.
 * @returns {function(object): Promise<void>} An async function that takes a shareConfig object.
 */
export const composeOnShare = ( data, methods ) => async( shareConfig ) => {
  const { isMobileDevice } = handleEmptyObjects( data );
  const { handleFlyoutAction } = handleEmptyObjects( methods );

  const list = shareConfig?.data;

  if( !list?.link || !handleFlyoutAction ){
    return;
  }

  const shareData = {
    title: list?.listTitle,
    url: list.link
  };

  // Mobile Native Web Share will not work on Firefox.
  // Fallback is regular overlay: https://developer.mozilla.org/en-US/docs/Web/API/Navigator/share
  if( global?.navigator?.share && isMobileDevice ){
    try {
      await global.navigator.share( shareData );
    }
    catch ( err ){
      if( err.name !== 'AbortError' ){
        // eslint-disable-next-line no-console
        console.error( 'Share failed:', err );
      }
    }
  }
  else {
    handleFlyoutAction( shareConfig );
  }
};

/**
 * Composable function to handle the edit action.
 * @param {object} data - Dependencies for the function. (Currently unused)
 * @param {object} methods - Methods to be called.
 * @param {function} methods.handleFlyoutAction - The function to call to open the edit flyout.
 * @returns {function(object, object): void} A function that takes an event and an editConfig object.
 */
export const composeOnEdit = ( methods ) => ( editConfig ) => {
  const { handleFlyoutAction } = handleEmptyObjects( methods );

  if( !handleFlyoutAction ){
    return;
  }

  handleFlyoutAction( editConfig );
};

/**
 * Defines the different modes for the MyListsForm flyout.
 * @readonly
 * @enum {string}
 */
export const MY_LIST_FORM_MODES = {
  EDIT: 'edit',
  SHARE: 'share'
};

/**
 * Property type definitions
 * @typedef MyListHeaderProps
 * @type {object}
 * @property {string} title - The main title for the list header
 * @property {string} subTitle - The subtitle under the main title
 * @property {string} shareIcon - Icon name for the share button
 * @property {string} editIcon - Icon name for the edit button
 * @property {object} myListsForm - MyListsForm configuration object
 * @property {object} shareAction - Action config for the share button
 * @property {object} saveAction - Action config for the save action in the overlay form
 * @property {object} shareDetails - Details for sharing the list
 * @property {object} editAction - Action config for the edit button
 * @property {object} copyAction - Action config for the copy link button
 * @property {Function} invokeMutation - Function to invoke GraphQL mutations
 * @property {string} id - Unique identifier for the list
 */
MyListHeader.propTypes = {
  /** The main title for the list header */
  title: PropTypes.string,
  /** The subtitle under the main title */
  subTitle: PropTypes.string,
  /** Icon name for the share button */
  shareIcon: PropTypes.string,
  /** Icon name for the edit button */
  editIcon: PropTypes.string,
  /** MyListsForm configuration object */
  myListsForm: PropTypes.object,
  /** Action config for the share button */
  shareAction: PropTypes.object,
  /** Action config for the save action in the overlay form */
  saveAction: PropTypes.object,
  /** Details for sharing the list */
  shareDetails: PropTypes.object,
  /** Action config for the edit button */
  editAction: PropTypes.object,
  /** Action config for the copy link button */
  copyAction: PropTypes.object,
  /** Function to invoke GraphQL mutations */
  invokeMutation: PropTypes.func,
  /** Unique identifier for the list */
  id: PropTypes.string
};

export default MyListHeader;
